import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";
import { get, post, put } from "../services/apiClient";

export const BookingStatus = {
  PENDING: "PENDING",
  CONFIRMED: "CONFIRMED",
  ACTIVE: "ACTIVE",
  COMPLETED: "COMPLETED",
  CANCELLED: "CANCELLED",
  REJECTED: "REJECTED",
} as const;
export type BookingStatus = (typeof BookingStatus)[keyof typeof BookingStatus];

export interface BookingRow {
  id: string;
  bookingRef: string;
  customerId: string;
  businessId: string;
  roomTypeId: string;
  packageId?: string | null;
  careType: string;
  checkinDate: string;
  checkoutDate: string;
  durationNights: number;
  guestCount: number | null;
  specialRequests: string | null;
  status: BookingStatus | string;
  createdAt: string;
  customer?: {
    user?: { phone: string; email: string | null };
    name?: string | null;
  } | null;
  business?: {
    id: string;
    nameEn: string;
    nameMl?: string | null;
    city: string;
    phone?: string | null;
    whatsapp?: string | null;
    address?: string | null;
  } | null;
  roomType?: {
    id: string;
    name: string;
    pricePerNight: number | string;
    price30Days?: number | string | null;
    price40Days?: number | string | null;
    durationPrices?:
      | { durationNights: number; price: number | string }[]
      | null;
  } | null;
  package?: {
    id: string;
    name: string;
    basePrice: number | string;
    durationNights: number;
  } | null;
  payments?: Array<{
    id: string;
    amountTotal: number | string;
    status: string;
    createdAt?: string;
  }>;
}

export const computeBookingTotal = (
  b: Pick<BookingRow, "package" | "roomType" | "durationNights">,
): number => {
  if (b.package?.basePrice != null) return Number(b.package.basePrice);
  const rt = b.roomType;
  if (!rt) return 0;
  const pricedDuration = (rt.durationPrices ?? []).find(
    (p) => p.durationNights === b.durationNights,
  );
  if (pricedDuration) return Number(pricedDuration.price);
  if (b.durationNights === 30 && rt.price30Days != null)
    return Number(rt.price30Days);
  if (b.durationNights === 40 && rt.price40Days != null)
    return Number(rt.price40Days);
  return 0;
};

export interface BookingListParams {
  page?: number;
  limit?: number;
  status?: BookingStatus;
  q?: string;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

interface BookingListResponse {
  data: BookingRow[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export const bookingKeys = {
  all: ["bookings"] as const,
  list: (p: BookingListParams) => [...bookingKeys.all, "list", p] as const,
  detail: (id: string) => [...bookingKeys.all, "detail", id] as const,
};

export const useBookingList = (
  params: BookingListParams = {},
  options?: Omit<UseQueryOptions<BookingListResponse>, "queryKey" | "queryFn">,
) =>
  useQuery<BookingListResponse>({
    queryKey: bookingKeys.list(params),
    queryFn: () => get<BookingListResponse>("/bookings", params),
    ...options,
  });

export const useBooking = (id: string | undefined) =>
  useQuery<BookingRow>({
    queryKey: bookingKeys.detail(id ?? ""),
    queryFn: () => get<BookingRow>(`/bookings/${id}`),
    enabled: !!id,
  });

export const useUpdateBookingStatus = () => {
  const qc = useQueryClient();
  return useMutation<
    BookingRow,
    Error,
    { id: string; status: BookingStatus; statusReason?: string }
  >({
    mutationFn: ({ id, status, statusReason }) =>
      put<BookingRow>(`/bookings/${id}/status`, { status, statusReason }),
    onSuccess: (_data, v) => {
      qc.invalidateQueries({ queryKey: bookingKeys.all });
      qc.invalidateQueries({ queryKey: bookingKeys.detail(v.id) });
      qc.invalidateQueries({ queryKey: ["dashboard", "stats"] });
    },
  });
};

export const useMarkBookingPaid = () => {
  const qc = useQueryClient();
  return useMutation<BookingRow, Error, { id: string; amount?: number }>({
    mutationFn: ({ id, amount }) =>
      post<BookingRow>(
        `/bookings/${id}/mark-paid`,
        amount != null ? { amount } : {},
      ),
    onSuccess: (_data, v) => {
      qc.invalidateQueries({ queryKey: bookingKeys.all });
      qc.invalidateQueries({ queryKey: bookingKeys.detail(v.id) });
      qc.invalidateQueries({ queryKey: ["dashboard", "stats"] });
      qc.invalidateQueries({ queryKey: ["subscription-stats"] });
    },
  });
};
